// The quiz (EDU-4, ARCH §29.5). PURE (ENG-1).
//
// Grading is a pure function of (quiz, answers) — the same shape a server needs to
// grade `QuizSubmit` and solo needs to grade locally (§29.5 "Authority"). It never
// returns an answer, only whether each submitted one was right, so `gradeQuiz`'s
// own return type cannot leak the key by construction.
//
// `gradePunnett` is written HERE, not inherited from a HER-11 that does not exist
// yet (v1.1, D-TF-193) — a genotype is a bare 2-character string ("Wb"), not a
// gene-table lookup, so this module needs no dependency on `heredity/`.
import type { QuestionData, QuizDef } from '../registry/schemas';

export type { QuestionData, QuizDef } from '../registry/schemas';

/** One submitted answer, positionally matched to `quiz.questions[i]`: a chosen
 *  option index (`choice`), a number (`numeric`), or the student's filled 2×2
 *  Punnett grid (`punnett`). */
export type QuizAnswer = number | readonly (readonly string[])[];

export interface QuizResult {
  readonly score: number;
  readonly n: number;
  readonly marks: readonly boolean[];
}

/** Normalize one allele pair ascending (§28.4's convention: the lower-sorting
 *  allele character first), so which parent contributed which allele never
 *  changes a cell's canonical form. */
function pair(x: string, y: string): string {
  return [x, y].sort().join('');
}

/** The expected 2×2 grid for a cross of two genotypes (each a 2-character allele
 *  string, e.g. "Wb"): row = `a`'s alleles, column = `b`'s alleles, each cell the
 *  normalized pair. `gradePunnett('Wb','Wb')` is the classic 1:2:1 square;
 *  `gradePunnett('WW','bb')` is uniformly `Wb`. */
export function gradePunnett(a: string, b: string): string[][] {
  return [...a].map((ai) => [...b].map((bi) => pair(ai, bi)));
}

function flatten(grid: readonly (readonly string[])[]): string[] {
  return grid.flatMap((row) => [...row]);
}

/** Same 4 cells, any order — a Punnett square is graded as a per-cell MULTISET,
 *  not a fixed layout: the square's own row/column axes are arbitrary (which
 *  parent's alleles run down vs. across), so a transposed-but-equivalent grid, or
 *  one with its cells in a different order, scores exactly like the canonical
 *  one. A single wrong cell changes the multiset and fails the match. */
function sameMultiset(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function gradeOne(q: QuestionData, a: QuizAnswer | undefined): boolean {
  switch (q.kind) {
    case 'choice':
      return typeof a === 'number' && a === q.answer;
    case 'numeric':
      return typeof a === 'number' && Math.abs(a - q.answer) <= q.tolerance;
    case 'punnett':
      return Array.isArray(a) && sameMultiset(flatten(gradePunnett(q.a, q.b)), flatten(a as readonly (readonly string[])[]));
  }
}

/** Grade a quiz attempt. Throws when the answer count does not match the question
 *  count (E-60) — refused, nothing recorded, exactly as an unknown-lesson or
 *  no-active-lesson `QuizSubmit` is refused at the wire layer (§29.9). Never
 *  reveals an answer: the result carries only pass/fail per question. */
export function gradeQuiz(quiz: QuizDef, answers: readonly QuizAnswer[]): QuizResult {
  if (answers.length !== quiz.questions.length) {
    throw new Error(`quiz has ${quiz.questions.length} questions, but ${answers.length} answers were submitted (E-60)`);
  }
  const marks = quiz.questions.map((q, i) => gradeOne(q, answers[i]));
  return { score: marks.filter(Boolean).length, n: quiz.questions.length, marks };
}
